// auth-cookie-manager.js
import Cookies from 'js-cookie';

const AUTH_TOKEN_COOKIE = 'firebase_auth_token';
const USER_DATA_COOKIE = 'firebase_user_data';
const TOKEN_EXPIRY_COOKIE = 'firebase_token_expiry';
const COOKIE_CHECK_TIMESTAMP = 'firebase_cookie_timestamp';

// Cookie options - adjust as needed
const cookieOptions = {
  expires: 7, // days
  secure: process.env.NODE_ENV === 'production', // Secure in production
  sameSite: 'strict'
};

export const saveAuthToCookies = (user) => {
  if (!user) return false;
  
  try {
    // Store the token
    if (user.stsTokenManager?.accessToken) {
      Cookies.set(AUTH_TOKEN_COOKIE, user.stsTokenManager.accessToken, cookieOptions);
      
      // Store expiration time
      if (user.stsTokenManager.expirationTime) {
        Cookies.set(TOKEN_EXPIRY_COOKIE, user.stsTokenManager.expirationTime, cookieOptions);
      }
    }
    
    // Store essential user data
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified
    };
    
    Cookies.set(USER_DATA_COOKIE, JSON.stringify(userData), cookieOptions);
    
    // Set timestamp for cookie integrity check
    Cookies.set(COOKIE_CHECK_TIMESTAMP, Date.now().toString(), cookieOptions);
    
    return true;
  } catch (error) {
    console.error('Error saving auth to cookies:', error);
    return false;
  }
};

export const getAuthFromCookies = () => {
  try {
    const token = Cookies.get(AUTH_TOKEN_COOKIE);
    const userDataStr = Cookies.get(USER_DATA_COOKIE);
    const expiryTime = Cookies.get(TOKEN_EXPIRY_COOKIE);
    const cookieTimestamp = Cookies.get(COOKIE_CHECK_TIMESTAMP);
    
    if (!token || !userDataStr || !cookieTimestamp) return null;
    
    const userData = JSON.parse(userDataStr);
    
    // Check if token has expired
    if (expiryTime && new Date().getTime() > parseInt(expiryTime)) {
      clearAuthCookies();
      return null;
    }
    
    return {
      token,
      user: userData,
      expiryTime: expiryTime ? parseInt(expiryTime) : null,
      cookieTimestamp: parseInt(cookieTimestamp)
    };
  } catch (error) {
    console.error('Error getting auth from cookies:', error);
    return null;
  }
};

export const clearAuthCookies = () => {
  Cookies.remove(AUTH_TOKEN_COOKIE);
  Cookies.remove(USER_DATA_COOKIE);
  Cookies.remove(TOKEN_EXPIRY_COOKIE);
  Cookies.remove(COOKIE_CHECK_TIMESTAMP);
};

export const isAuthenticated = () => {
  const authData = getAuthFromCookies();
  return authData !== null && authData.token && authData.user;
};

// New function to detect if cookies were cleared
export const checkCookieIntegrity = () => {
  try {
    // Check if any of our cookies are missing when they should exist
    const token = Cookies.get(AUTH_TOKEN_COOKIE);
    const userDataStr = Cookies.get(USER_DATA_COOKIE);
    const cookieTimestamp = Cookies.get(COOKIE_CHECK_TIMESTAMP);
    
    // If we have some but not all cookies, it indicates tampering or partial clearing
    const hasPartialCookies = Boolean(token || userDataStr || cookieTimestamp);
    const hasAllCookies = Boolean(token && userDataStr && cookieTimestamp);
    
    if (hasPartialCookies && !hasAllCookies) {
      console.warn("Cookie integrity check failed: Some auth cookies missing");
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error checking cookie integrity:", error);
    return false;
  }
};